import { useState, useEffect } from 'react';
import { 
  Link, 
  useLocation,
  useNavigate
} from 'react-router-dom';
import { 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  styled,
  Drawer,
  Box,
  Divider,
  InputAdornment,
  IconButton,
  useMediaQuery,
  ThemeProvider,
  createTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography
} from '@mui/material';
import { 
  Home, 
  Settings, 
  ArrowBack,
  Menu,
  Lock,
  Person,
  GetApp
} from '@mui/icons-material';
import { grey } from '@mui/material/colors';
import { fetchData, isUsernameTaken, updateEmployee } from '../api/github';
import { hashPassword } from '../utils/auth';
import InstallPrompt from './InstallPrompt';

const StyledListItem = styled(ListItem)(({ theme, selected }) => ({
  backgroundColor: selected ? theme.palette.action.selected : 'transparent',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const theme = createTheme();

export default function Sidebar({ showMenuButton = true }) {
  const location = useLocation();
  const navigate = useNavigate();
  const userRole = localStorage.getItem('userRole');
  const userId = localStorage.getItem('userId');
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [installPromptOpen, setInstallPromptOpen] = useState(false);
  const [currentUsername, setCurrentUsername] = useState(localStorage.getItem('userName'));
  const [currentPassword, setCurrentPassword] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const isActive = (path) => {
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  const handleSettingsOpen = () => {
    setSettingsOpen(true);
  };

  const handleSettingsClose = () => {
    setSettingsOpen(false);
    setNewUsername('');
    setNewPassword('');
    setConfirmPassword('');
  };


  const loadEmployees = async () => {
    try {
      const data = await fetchData('employees.json');
      setCurrentPassword(data.filter(e => e.role == 'admin')[0].password);
    }
    finally {}
  };

  useEffect(() => {
    loadEmployees();
  }, []);

  const handleInstallPromptOpen = () => {
    setInstallPromptOpen(true);
  };

  const handleInstallPromptClose = () => {
    setInstallPromptOpen(false);
  };

  const handleSaveSettings = async () => {
    try {
      if (newPassword && newPassword !== confirmPassword) {
        alert('Пароллар мос келмаяпти');
        return;
      }

      if (newUsername && newUsername !== currentUsername) {
        const isTaken = await isUsernameTaken(newUsername, userId);
        if (isTaken) {
          alert('Бу логин банд');
          return;
        }
      }

      const updatedData = {};
      if (newUsername && newUsername !== currentUsername) {
        updatedData.username = newUsername;
        localStorage.setItem('username', newUsername);
        setCurrentUsername(newUsername);
      }
      if (newPassword) {
        updatedData.password = hashPassword(newPassword);
      }

      if (Object.keys(updatedData).length > 0) {
        await updateEmployee(userId, updatedData);
        alert('Маълумотлар муваффақиятли озгарди');
      } else {
        alert('Маълумотларда хеч қандай ўзгариш йўқ');
      }

      handleSettingsClose();
    } catch (error) {
      console.error('Маълумотлар сақлашда хатолик:', error);
      alert(error.message || 'Озгаришларни киритшда хатолик');
    }
  };

  useEffect(() => {
    if (isMobile) {
      setMobileOpen(false);
    }
  }, [location, isMobile]);

  const drawerContent = (
    <div style={{ width: 250, backgroundColor: '#f5f5f5', height: '100vh' }}>
      <List sx={{mt: isMobile ? 5 : 0}}>
        <StyledListItem 
          button="true" 
          component={Link} 
          to={userRole === 'admin' ? "/admin" : "/employee"}
          selected={isActive(userRole === 'admin' ? '/admin' : "/employee")}
        >
          <ListItemIcon>
            <Home color={isActive(userRole === 'admin' ? '/admin' : "/employee") ? 'primary' : 'inherit'} />
          </ListItemIcon>
          <ListItemText 
            primary="Бош сахива" 
            primaryTypographyProps={{
              color: isActive(userRole === 'admin' ? '/admin' : "/employee") ? 'primary' : 'inherit',
              fontWeight: isActive(userRole === 'admin' ? '/admin' : "/employee") ? 'medium' : 'normal'
            }} 
          />
        </StyledListItem>
        
        {userRole === 'admin' && (
          <StyledListItem 
            button="true" 
            onClick={handleSettingsOpen}
            selected={isActive('/settings')}
          >
            <ListItemIcon>
              <Settings color={isActive('/settings') ? 'primary' : 'inherit'} />
            </ListItemIcon>
            <ListItemText 
              primary="Сўзламалар" 
              primaryTypographyProps={{
                color: isActive('/settings') ? 'primary' : 'inherit',
                fontWeight: isActive('/settings') ? 'medium' : 'normal'
              }} 
            />
          </StyledListItem>
        )}
        
        <StyledListItem 
          button="true" 
          onClick={handleInstallPromptOpen}
        >
          <ListItemIcon>
            <GetApp color={isActive('/install') ? 'primary' : 'inherit'} />
          </ListItemIcon>
          <ListItemText 
            primary="Иловани ўрнатиш" 
            primaryTypographyProps={{
              color: isActive('/install') ? 'primary' : 'inherit',
              fontWeight: isActive('/install') ? 'medium' : 'normal'
            }} 
          />
        </StyledListItem>
        
        <StyledListItem 
          button="true" 
          onClick={handleLogout}
        >
          <ListItemIcon>
            <ArrowBack color={isActive('/') ? 'primary' : 'inherit'} />
          </ListItemIcon>
          <ListItemText 
            primary="Аккаунтдан чиқиш" 
            primaryTypographyProps={{
              color: isActive('/') ? 'primary' : 'inherit',
              fontWeight: isActive('/') ? 'medium' : 'normal'
            }} 
          />
        </StyledListItem>
      </List>

      {/* Диалог настроек */}
      <Dialog open={settingsOpen} onClose={handleSettingsClose} fullWidth maxWidth="sm">
        <DialogTitle>Фойдаланувчи маълумотлари</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Жорий маълумотлар:
            </Typography>
            <TextField
              label="Логин"
              fullWidth
              margin="normal"
              value={currentUsername}
              InputProps={{
                readOnly: true,
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <TextField
              label="Пароль"
              fullWidth
              margin="normal"
              value={currentPassword}
              InputProps={{
                readOnly: true,
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle1" gutterBottom>
            Янги маълумотлар:
          </Typography>
          <TextField
            label="Янги логин"
            fullWidth
            margin="normal"
            value={newUsername}
            onChange={(e) => setNewUsername(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Янги пароль"
            fullWidth
            margin="normal"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Парольни тасдиқланг"
            fullWidth
            margin="normal"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={newPassword !== confirmPassword}
            helperText={newPassword !== confirmPassword ? "Пароллар мос келмайди" : ""}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock />
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSettingsClose}>Бекор қилиш</Button>
          <Button 
            onClick={handleSaveSettings} 
            variant="contained" 
            color="primary"
            disabled={!newUsername && !newPassword}
          >
            Сақлаш
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );

  return (
    <>
      {isMobile && showMenuButton && (
        <IconButton
          color="white"
          aria-label="open drawer"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{ 
            position: 'fixed',
            top: 5,
            left: 15,
            color: 'white',
            zIndex: theme.zIndex.drawer + 1,
            bgcolor: 'rgba(0, 0, 0, 0.2)',
            '&:hover': {
              bgcolor: grey[800],
            }
          }}
        >
          <Menu />
        </IconButton>
      )}
      
      <ThemeProvider theme={theme}>
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            width: 250,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: 250,
              boxSizing: 'border-box',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      </ThemeProvider>
      
      <InstallPrompt open={installPromptOpen} onClose={handleInstallPromptClose} />
    </>
  );
}